import { Box, Flex, Grid, useDisclosure, useToast } from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getFromUrl, serverRequest } from '../../api/Api';
import { PrimaryButton, TextButton } from '../../components/Buttons/Buttons';
import { Icon } from '../../components/Icons';
import { TextField } from '../../components/Inputs/TextField';
import ModalView from '../../components/Modal';
import Pagination from '../../components/Pagination';
import PopoverMenu from '../../components/Popover';
import Referrals from '../../components/Resources/Referrals';
import ResourcesItems from '../../components/Resources/ResourcesItems';
import { Body, Headline } from '../../components/Texts/Texts';
import { colors } from '../../theme/colors';

const ResourcesSection = () => {
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [folders, setFolders] = useState<any>([]);
  const [files, setFiles] = useState<any>([]);
  const [folderName, setFolderName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const fileInput = React.useRef<null | HTMLInputElement>(null);
  const [pagination, setPagination] = useState<any>(null);

  // Pagination settings
  const currentPage = parseInt(searchParams.get('page') || '1', 10);

  const handlePageChange = (page: number | string) => {
    if (typeof page === 'number') {
      setSearchParams({ page: page.toString() });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchFiles(), fetchFolders()]).finally(() => {
      setLoading(false);
    });
  }, [currentPage]);

  const fetchFiles = () => {
    // Backend uses 0-based indexing, UI uses 1-based
    const backendPage = currentPage - 1;
    return getFromUrl(`resources/all/files?page=${backendPage}`).then((res) => {
      setFiles(res?.data || []);
      setPagination(res?.pagination || null);
    });
  };
  const fetchFolders = () => {
    return getFromUrl('resources/all/folders').then((res) => {
      setFolders(res);
    });
  };

  const createFolder = () => {
    setIsLoading(true);
    serverRequest({
      endpoint: 'resources/folder',
      body: { folderName },
      method: 'post'
    }).then((res) => {
      setIsLoading(false);
      if (res?.data) {
        fetchFolders();
        toast({
          position: 'top',
          title: 'Success',
          description: `Folder created successfully`,
          status: 'success',
          duration: 3000,
          isClosable: true
        });
      } else {
        toast({
          position: 'top',
          title: 'Error',
          description: 'Error creating folder',
          status: 'error',
          duration: 3000,
          isClosable: true
        });
      }
      onClose();
    });
  };
  const selectFile = () => {
    fileInput.current?.click();
  };

  const onChangeFile = (event: any) => {
    event.preventDefault();
    setUploading(true);
    const files = event.target.files;
    const formData = new FormData();
    for (const file of files) {
      formData.append('files', file);
    }
    serverRequest({
      endpoint: 'resources/upload/files',
      body: formData,
      method: 'post',
      type: 'formData'
    })
      .then((res) => {
        if (res?.results) {
          fetchFiles();
          toast({
            position: 'top',
            title: 'Success',
            description: `Files uploaded successful`,
            status: 'success',
            duration: 3000,
            isClosable: true
          });
        } else {
          toast({
            position: 'top',
            title: 'Error',
            description: 'Error uploading files',
            status: 'error',
            duration: 3000,
            isClosable: true
          });
        }
      })
      .catch((error) => {
        toast({
          position: 'top',
          title: 'Error',
          description: 'Error uploading files',
          status: 'error',
          duration: 3000,
          isClosable: true
        });
      })
      .finally(() => {
        setUploading(false);
      });
  };

  return (
    <>
      <style>{`
        @keyframes resourceUploadSpin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      <Grid
        gridTemplateColumns='2.4fr 1fr'
        gap='56px'
        mt='32px'
        pb='64px'
        pr='60px'
      >
        <Box>
          <PopoverMenu
            trigger={
              <Flex
                cursor={uploading ? 'not-allowed' : 'pointer'}
                alignItems='center'
                justifyContent='center'
                color='deep_blue'
                mr='26px'
                bg='accent'
                w='244px'
                h='55px'
                borderRadius='12px'
                mb='36px'
                px='20px'
                opacity={uploading ? 0.6 : 1}
                pointerEvents={uploading ? 'none' : 'auto'}
              >
                {uploading ? (
                  <div
                    style={{
                      width: '24px',
                      height: '24px',
                      border: '3px solid rgba(255, 255, 255, 0.3)',
                      borderTop: '3px solid white',
                      borderRadius: '50%',
                      animation: 'resourceUploadSpin 0.8s linear infinite',
                      marginRight: '26px'
                    }}
                  />
                ) : (
                  <>
                    <Body
                      color='white'
                      textTransform='uppercase'
                      size='14px'
                      mr='26px'
                      fontWeight={500}
                    >
                      Add new
                    </Body>
                    <Icon name='Upload' color={colors.white} size={20} filled />
                  </>
                )}
              </Flex>
            }
            top='200px'
            w='334px'
            placement='top-start'
          >
            {(onClose: any) => (
              <Box fontSize='19px' fontWeight={500} color='gray' pt='15px'>
                <Flex
                  onClick={onOpen}
                  pt='30px'
                  pb='14px'
                  justifyContent='center'
                  cursor='pointer'
                  position='relative'
                >
                  Create Folder
                </Flex>
                <Flex
                  onClick={() => {
                    if (!uploading) {
                      onClose();
                      selectFile();
                    }
                  }}
                  py='14px'
                  justifyContent='center'
                  cursor={uploading ? 'not-allowed' : 'pointer'}
                  opacity={uploading ? 0.5 : 1}
                  pointerEvents={uploading ? 'none' : 'auto'}
                >
                  {uploading ? 'Uploading...' : 'Upload File'}
                </Flex>
              </Box>
            )}
          </PopoverMenu>
          <Box>
            {!loading && !uploading ? (
              <>
                <ResourcesItems
                  type='folders'
                  data={folders}
                  refetch={fetchFolders}
                />
                <ResourcesItems
                  type='files'
                  data={files}
                  refetch={fetchFiles}
                />
                {pagination && pagination.totalPages > 1 && (
                  <Pagination
                    currentPage={currentPage}
                    totalCount={pagination.totalItems}
                    pageSize={pagination.pageSize}
                    onPageChange={handlePageChange}
                  />
                )}
              </>
            ) : (
              <Flex justifyContent='center' alignItems='center' minH='200px'>
                <div
                  style={{
                    width: '48px',
                    height: '48px',
                    border: '4px solid rgba(0, 0, 0, 0.1)',
                    borderTop: '4px solid #3B82F6',
                    borderRadius: '50%',
                    animation: 'resourceUploadSpin 0.8s linear infinite'
                  }}
                />
              </Flex>
            )}

            <input
              multiple={true}
              type='file'
              id='file'
              ref={fileInput}
              style={{ display: 'none' }}
              onChange={onChangeFile}
            />
          </Box>
        </Box>
        <Referrals />
        <ModalView
          isOpen={isOpen}
          onClose={onClose}
          bg='gray_50'
          maxW='600px'
          noCloseBtn
        >
          <Box bg='gray_50' px='86px' py='53px' borderRadius='8px'>
            <Headline size='19px' color='gray' mb='28px'>
              Create a new resource folder
            </Headline>
            <form onSubmit={createFolder}>
              <TextField
                label='Folder name'
                value={folderName}
                onChange={setFolderName}
                bg='white'
                required={true}
              />
              <PrimaryButton
                label='Create Folder'
                isLoading={isLoading}
                type={'submit'}
                onClick={createFolder}
              />
              <TextButton label='cancel' mt='24px' onClick={onClose} />
            </form>
          </Box>
        </ModalView>
      </Grid>
    </>
  );
};

export default ResourcesSection;
